import type { AccountInterface } from '@/interfaces/account';
import type { AccountBase } from '@/models/AccountViewModel';
import { mapAccountToAccountInterface } from '@/utils/mappers';

export interface AccountsGroupedByType {
  classificationId: number;
  type: string;
  accounts: AccountInterface[];
  isAsset: boolean;
}

interface RawAccountsGroup {
  classificationId: number;
  type: string;
  accounts: AccountBase[];
  isAsset: boolean;
}

const sumBalances = (accounts: AccountBase[]) =>
  accounts.reduce((total, account) => total + account.Balance, 0);

// Assets list the largest balance first; liabilities list the largest debt
// (the most negative balance) first.
const compareBalances = (isAsset: boolean, a: number, b: number) =>
  isAsset ? b - a : a - b;

function sortSection(
  rawGroups: RawAccountsGroup[],
  isAsset: boolean,
): AccountsGroupedByType[] {
  return rawGroups
    .filter((group) => group.isAsset === isAsset)
    .sort((a, b) =>
      compareBalances(
        isAsset,
        sumBalances(a.accounts),
        sumBalances(b.accounts),
      ),
    )
    .map((group) => ({
      ...group,
      accounts: [...group.accounts]
        .sort((a, b) => compareBalances(isAsset, a.Balance, b.Balance))
        .map(mapAccountToAccountInterface),
    }));
}

export function groupAccountsByType(
  accounts: AccountBase[],
): AccountsGroupedByType[] {
  const rawGroups = accounts.reduce<RawAccountsGroup[]>((groups, account) => {
    const currentGroup = groups.find(
      (group) =>
        group.classificationId === account.ClassificationId &&
        group.isAsset === account.IsAsset,
    );

    if (currentGroup) {
      currentGroup.accounts.push(account);
    } else {
      groups.push({
        classificationId: account.ClassificationId,
        type: account.ClassificationName,
        accounts: [account],
        isAsset: account.IsAsset,
      });
    }

    return groups;
  }, []);

  return [...sortSection(rawGroups, true), ...sortSection(rawGroups, false)];
}
